/* Add photo-style controls to content carousels without replacing native scrolling. */
(() => {
  const selector = [
    'slider-component > [id^="Slider-"]',
    'slideshow-component > .slider',
    '.simple-slideshow__inner > .simple-slideshow__track',
    'product-recommendations.related-products > .product-grid',
    'same-color-products > .wolf-color-track',
    'wolf-cart-colors > [data-cart-color-products]',
    '.article-template__aside-list',
    '.menu-drawer-child-list',
    '.registry-form .product-grid-outer',
    '.registry-form #selected-products-list',
    '[data-wolf-carousel-track]',
  ].join(',');
  const instances = new Map();
  let nextId = 0;

  function enhance(track) {
    if (instances.has(track) || track.closest('media-gallery, .announcement-bar')) return;
    const template = document.getElementById('WolfSliderArrows');
    if (!template) return;
    // Drawer collection cards sit inside a summary; keep buttons outside its toggle.
    const host = track.matches('.menu-drawer-child-list')
      ? track.parentElement.closest('li') || track.parentElement
      : track.parentElement;
    const simpleSlideshow = host.matches('.simple-slideshow__inner');
    const external = simpleSlideshow || host.matches('same-color-products, wolf-cart-colors');
    const controls = external
      ? host.querySelector(simpleSlideshow ? '.simple-slideshow__controls' : '.wolf-color-controls')
      : template.content.firstElementChild.cloneNode(true);
    if (!controls) return;
    const previous = controls.querySelector('button:first-child');
    const next = controls.querySelector('button:last-child');
    if (!previous || !next) return;
    const events = new AbortController();
    const options = { signal: events.signal };
    const legacy = external ? null : host.querySelector(':scope > .slider-buttons');
    const slideshow = host.matches('slideshow-component') ? legacy : null;
    let frame = 0;
    let positions = [];
    let rtl = false;
    let observedMedia;

    host.classList.add('wolf-carousel-host');
    track.classList.add('wolf-carousel-track');
    controls.classList.add('wolf-carousel-controls');
    previous.classList.add('wolf-slider-arrow', 'wolf-slider-arrow--previous');
    next.classList.add('wolf-slider-arrow', 'wolf-slider-arrow--next');
    if (!track.id) track.id = `WolfCarousel-${++nextId}`;
    previous.setAttribute('aria-controls', track.id);
    next.setAttribute('aria-controls', track.id);
    if (!track.hasAttribute('tabindex')) track.tabIndex = 0;
    legacy?.classList.add('wolf-carousel-legacy-controls');
    // Panels and the homepage slideshow retain their existing scroll handlers.
    host.append(controls);

    function update() {
      frame = 0;
      const style = getComputedStyle(track);
      const maximum = track.scrollWidth - track.clientWidth;
      const overflow = track.clientWidth > 0 && maximum > 2 && /auto|scroll/.test(style.overflowX);
      controls.hidden = !overflow;
      if (!overflow) return;
      rtl = style.direction === 'rtl';
      const offset = Math.abs(track.scrollLeft);
      const trackRect = track.getBoundingClientRect();
      const hostRect = host.getBoundingClientRect();
      const cards = track.matches('.product-grid-outer') ? [...track.querySelectorAll('.product')]
        : track.matches('.wolf-pdp-content__scroller') ? [...track.querySelectorAll('.wolf-detail-card')]
          : [...track.children];
      const visibleCards = cards.filter((card) => card.getBoundingClientRect().width > 0);
      const inset = parseFloat(rtl ? style.scrollPaddingRight : style.scrollPaddingLeft)
        || parseFloat(rtl ? style.paddingRight : style.paddingLeft) || 0;
      positions = [0, ...visibleCards.map((card) => {
        const rect = card.getBoundingClientRect();
        const distance = rtl ? trackRect.right - rect.right : rect.left - trackRect.left;
        return Math.max(0, Math.min(maximum, offset + distance - inset));
      }), maximum].sort((a, b) => a - b).filter((value, index, all) => !index || value - all[index - 1] > 2);
      if (!external) {
        previous.disabled = !slideshow && offset <= 2;
        next.disabled = !slideshow && offset >= maximum - 2;
      }
      const card = visibleCards[0];
      const media = card?.querySelector('.wolf-color-card__media, .card__inner, .media, .collection-image-wrapper, .article-template__aside-card-image, img') || card;
      if (media !== observedMedia) {
        if (observedMedia) resize.unobserve(observedMedia);
        if (media) resize.observe(media);
        observedMedia = media;
      }
      const mediaRect = media?.getBoundingClientRect();
      const center = mediaRect ? mediaRect.top + mediaRect.height / 2 : trackRect.top + trackRect.height / 2;
      controls.style.top = `${center - hostRect.top + host.scrollTop}px`;
      controls.style.left = `${trackRect.left - hostRect.left + host.scrollLeft}px`;
      controls.style.width = `${trackRect.width}px`;
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }

    function move(direction) {
      if (slideshow) {
        slideshow.querySelector(direction > 0 ? 'button[name="next"]' : 'button[name="previous"]')?.click();
        return;
      }
      update();
      const offset = Math.abs(track.scrollLeft);
      const target = direction > 0 ? positions.find((value) => value > offset + 2) : positions.findLast((value) => value < offset - 2);
      if (target === undefined) return;
      track.scrollTo({ left: target * (rtl ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    if (!external) {
      previous.addEventListener('click', () => move(-1), options);
      next.addEventListener('click', () => move(1), options);
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      }, options);
    } else if (simpleSlideshow) {
      track.addEventListener('keydown', (event) => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        (event.key === 'ArrowRight' ? next : previous).click();
      }, options);
    }
    track.addEventListener('scroll', schedule, { ...options, passive: true });
    track.addEventListener('load', schedule, { ...options, capture: true });
    const resize = new ResizeObserver(schedule);
    resize.observe(track);
    resize.observe(host);
    const mutation = new MutationObserver(schedule);
    mutation.observe(track, { childList: true, subtree: true });
    instances.set(track, () => {
      events.abort();
      resize.disconnect();
      mutation.disconnect();
      cancelAnimationFrame(frame);
      if (!external) controls.remove();
      legacy?.classList.remove('wolf-carousel-legacy-controls');
    });
    schedule();
  }

  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root.matches?.(selector)) enhance(root);
    root.querySelectorAll(selector).forEach(enhance);
  }

  scan(document);
  // Shopify inserts recommendations and cart contents after the initial page load.
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach(scan));
    instances.forEach((cleanup, track) => {
      if (!track.isConnected) { cleanup(); instances.delete(track); }
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
